import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, MailCheck } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { container } from "@/lib/constants";

export default function AccessPending() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user && user.status === "verified") navigate("/deals");
  }, [user, loading, navigate]);

  const emailConfirmed = user?.email_confirmed;
  const declined = user?.status === "declined";

  return (
    <PublicLayout>
      <SEO title="Access pending" noindex />
      <section className={`${container} py-24`}>
        <GlassCard className="max-w-2xl mx-auto p-10 lg:p-14">
          <div className="flex items-center gap-3">
            {declined ? <StatusBadge status="declined" /> : <StatusBadge status="pending" label="Under review" />}
          </div>
          {declined ? (
            <>
              <h1 className="mt-6 pdg-serif text-4xl text-foreground">An update on your application</h1>
              <p className="mt-4 text-base text-muted-foreground leading-8">
                After careful review, we are unable to extend access at this time. We appreciate your interest in the desk.
              </p>
            </>
          ) : (
            <>
              <h1 className="mt-6 pdg-serif text-4xl text-foreground">Your application is under review</h1>
              <p className="mt-4 text-base text-muted-foreground leading-8" data-testid="access-pending-status">
                Thank you. The desk is verifying your principal status. We will be in touch within
                two business days.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <MailCheck size={18} className={emailConfirmed ? "text-[hsl(145_38%_62%)]" : "text-[hsl(var(--pdg-gold))]"} strokeWidth={1.5} />
                  <p className="text-sm text-foreground/80">
                    {emailConfirmed ? "Email confirmed." : "Please confirm your email using the link we sent. Your review begins once confirmed."}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={18} className="text-muted-foreground" strokeWidth={1.5} />
                  <p className="text-sm text-foreground/80">Verification typically completes within two business days.</p>
                </div>
              </div>
            </>
          )}
          <div className="mt-10 pt-6 border-t border-border">
            <Link to="/contact" data-testid="access-pending-contact-link" className="text-sm text-[hsl(var(--pdg-gold))] pdg-link-underline">
              Contact the desk
            </Link>
          </div>
        </GlassCard>
      </section>
    </PublicLayout>
  );
}
